import { getMeta } from "app/utils/meta";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
} from "app/components/accordion";
import Link from "app/components/i18n/Link";
import Layout from "app/components/layout";

export const meta = getMeta({
  title: "Privacy Policy | Protecting and respecting your data",
});

const DataPrivacyPolicy = () => {
  return (
    <Layout headerIsOnTop={false} enableHeaderShrinkingOnScroll={false}>
      <header className="bg-primary">
        <div className="zbooni-layout pb-7 sm:pb-9 pt-24 sm:pt-32">
          <h1 className="text-2xl font-extrabold text-white xs:text-[28px] sm:text-[32px]">
            Privacy Policy
          </h1>
        </div>
      </header>
      <div className="bg-white text-sm sm:text-base py-8">
        <div className="zbooni-layout">
          <div className="flex flex-col gap-y-7">
            <div className="flex flex-col italic">
              <p>Effective Date on: July 30, 2023</p>
              <p>Last updated on: July 31, 2023</p>
            </div>
            <p>Your privacy is extremely important to us.</p>
            <p>
              This Data Privacy Policy informs you about how we collect, use,
              disclose, manage and protect any personal data you provide to us
              when accessing or using our website with URL{" "}
              <Link to="/" className="text-blue underline">
                https://www.zbooni.com
              </Link>{" "}
              (including any subdomains thereof) as well as any other media
              form, media channel, mobile website or mobile application related,
              linked, or otherwise connected thereto (collectively, the “
              <span className="font-bold">Zbooni Platform</span>”), what we do
              with it and how you can control what personal data we collect from
              you. It applies to any individual visiting or using the Zbooni
              Platform (whether as a Customer or Merchant or otherwise) or
              otherwise providing any personal data to us (“
              <span className="font-bold">User</span>”, “
              <span className="font-bold">you</span>”).
            </p>
            <p>Please read it carefully.</p>
            <p>
              Unless defined otherwise, all definitions included in our Terms
              and Conditions will apply to this Data Privacy Policy.
            </p>
          </div>
          <Accordion
            active="who-we-are-and-how-to-contact-us"
            scrollToDiv={true}
          >
            <AccordionItem
              id="who-we-are-and-how-to-contact-us"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>1.</span>
                  <span>Who We Are and How to Contact Us</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="who-we-are-and-how-to-contact-us"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>1.1.</span>
                  <span>
                    The Zbooni Platform is owned and operated by Zbooni DMCC,
                    located at Mazaya Business Avenue AA1, Office 3205, Jumeria
                    Lake Towers, Dubai, United Arab Emirates, +971 4 5579133 (“
                    <span className="font-bold">Zbooni</span>” or “
                    <span className="font-bold">We</span>”).
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="applicable-terms-and-consent"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>2.</span>
                  <span>Applicable Terms and Consent</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="applicable-terms-and-consent"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>2.1.</span>
                  <span>
                    When you access or use the Zbooni Platform, or otherwise
                    provide any personal data to us, you agree to this Data
                    Privacy Policy, including the manner in which we use your
                    personal data. If you do not agree to this Data Privacy
                    Policy, or to any changes we subsequently make to this Data
                    Privacy Policy, you must immediately stop using the Zbooni
                    Platform or otherwise providing any information to us.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>2.2.</span>
                  <span>
                    Further, we reserve the right to change, in our sole
                    discretion, the terms of this Data Privacy Policy at any
                    time by publishing the varied terms on the Zbooni Platform,
                    with or without notice.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>2.3.</span>
                  <span>
                    You are responsible for reviewing regularly information
                    posted online to obtain timely notice of such changes. As
                    such, we recommend that you check the Zbooni Platform
                    regularly to ensure you are aware of our current terms. We
                    do not undertake to keep our Zbooni Platform up-to-date and
                    we are not liable if any content is inaccurate or
                    out-of-date. Your continued use of the Zbooni Platform after
                    changes are posted constitutes your acceptance of the Data
                    Privacy Policy as modified by the posted changes.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>2.4.</span>
                  <span>
                    Regardless of later updates or changes to our Data Privacy
                    Policy, we will never use the information you submit under
                    our current Data Privacy Policy in a new way without first
                    providing you an opportunity to opt-out or otherwise prevent
                    that use.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="age-of-consent-and-third-party-services"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>3.</span>
                  <span>Age of Consent and Third Party Services</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="age-of-consent-and-third-party-services"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>3.1.</span>
                  <span>
                    The Zbooni Platform is intended for users who are at least
                    18 years old. Persons under the age of 18 are not permitted
                    to register for the Zbooni Platform. All users who are
                    minors in the jurisdiction in which they reside (generally
                    under the age of 18) must have the permission of, and be
                    directly supervised by, their parent or guardian to use the
                    Zbooni Platform. If you are a minor, you must have your
                    parent or guardian read and agree to this Data Privacy
                    Policy prior to you using the Zbooni Platform.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>3.2.</span>
                  <span>
                    You need to create an account to access and use the Zbooni
                    Platform and in doing so, you must provide us with accurate
                    and complete information as prompted by the registration
                    process.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>3.3.</span>
                  <span>
                    We may allow you to log in to the Zbooni Platform through
                    various online third-party services, such as social media
                    and social networking services like Google, Facebook or
                    Twitter (“
                    <span className="font-bold">Third Party Services</span>”).
                    In order to use these features and capabilities, we may ask
                    you to authenticate, register for, or log into Third Party
                    Services on the websites of their respective providers. As
                    part of this integration, the Third Party Services will
                    provide us with access to certain information you have
                    provided to them, and we will use, store, and disclose such
                    information in accordance with this Data Privacy Policy.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>3.4.</span>
                  <span>
                    Third Party Services use, store, and disclose your
                    information solely in accordance with their own policies. We
                    assume no responsibility or liability for the privacy
                    practices or any other actions of any third-party website or
                    service that may be enabled within our services or of which
                    a link is included on the Zbooni Platform. Further, we
                    assume no responsibility or liability for any damage or loss
                    caused or alleged to be caused by or in connection with the
                    use of or reliance on any such Third Party Services.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="personal-data-we-collect"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>4.</span>
                  <span>Personal Data We Collect</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel id="personal-data-we-collect" className="border-b">
              <div className="mb-10 flex flex-col gap-y-7">
                <div className="pl-6 sm:pl-10">
                  <p className="flex gap-x-5 mb-3">
                    <span>4.1.</span>
                    <span>
                      To let us operate the Zbooni Platform effectively and to
                      let you use the Zbooni Platform, we may collect, use, and
                      store the personal data described below. We also disclose
                      certain information to third parties, as described further
                      below. The information we collect is received directly or
                      indirectly from you and generated in the course of your
                      use of the Zbooni Platform. The data we collect is not
                      used for marketing or any other purposes except as
                      specified in this Privacy Policy.
                    </span>
                  </p>
                  <ul className="flex flex-col gap-y-3 list-disc pl-20 sm:pl-24">
                    <li>
                      <span className="font-bold">Identity Data</span> includes
                      name
                    </li>
                    <li>
                      <span className="font-bold">Contact Data</span> includes
                      country of residence, address, email address and telephone
                      numbers
                    </li>
                    <li>
                      <span className="font-bold">Financial Data</span> includes
                      bank account and payment card details
                    </li>
                    <li>
                      <span className="font-bold">Transaction Data</span>{" "}
                      includes details of services you have purchased/sold
                      through the Zbooni Platform
                    </li>
                    <li>
                      <span className="font-bold">Technical Data</span> includes
                      internet protocol (IP) address, your login data, browser
                      type and version, time zone setting and location, browser
                      plug-in types and versions, operating system, and other
                      technology on the devices you use to access the Site.
                    </li>
                    <li>
                      <span className="font-bold">
                        Account Registration Data
                      </span>{" "}
                      includes certain information in connection with the
                      account registration process such as whether you
                      registered using any Third Party Services and whether you
                      verified your email address or phone number.
                    </li>
                    <li>
                      <span className="font-bold">Usage Data</span> includes
                      information about how you use our Zbooni Platform and our
                      services.
                    </li>
                    <li>
                      <span className="font-bold">Login Data</span> includes
                      information regarding logins and activity conducted during
                      that log in.
                    </li>
                    <li>
                      <span className="font-bold">
                        Marketing and Communications Data
                      </span>{" "}
                      includes your preferences in receiving marketing and your
                      communication preferences.
                    </li>
                  </ul>
                </div>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>4.2.</span>
                  <span>
                    We also get information about you and your activity outside
                    the Zbooni Platform from our affiliates, advertisers,
                    partners and other third parties we work with. For example,
                    online advertisers or third parties share information with
                    us to measure, report on or improve the performance of ads,
                    or to figure out what kinds of ads to show you.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>4.3.</span>
                  <span>
                    We also collect, use and share Aggregated Data such as
                    statistical or demographic data for any purpose, including
                    Zbooni Insights. Aggregated Data could be derived from your
                    personal data but is not considered personal data in law as
                    this data will not directly or indirectly reveal your
                    identity. For example, we may aggregate your Usage Data to
                    calculate the percentage of users accessing a specific
                    website feature. However, if we combine or connect
                    Aggregated Data with your personal data so that it can
                    directly or indirectly identify you, we treat the combined
                    data as personal data which will be used in accordance with
                    this Data Privacy Policy.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="how-we-collect-your-personal-data"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>5.</span>
                  <span>How We Collect Your Personal Data</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="how-we-collect-your-personal-data"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <div className="pl-6 sm:pl-10">
                  <p className="flex gap-x-5 mb-3">
                    <span>5.1.</span>
                    <span>
                      We collect personal data through one or more of the
                      following ways:
                    </span>
                  </p>
                  <ul className="flex flex-col gap-y-3 list-disc pl-14 sm:pl-20">
                    <li>
                      when you visit the Zbooni Platform we collect data
                      regarding the particular pages you have visited or viewed,
                      the amount of time you spent on the Zbooni Platform, the
                      errors you experienced, your mobile device ID and IP
                      address.
                    </li>
                    <li>
                      when you register your account we collect Identity Data,
                      Contact Data and Profile Data
                    </li>
                    <li>
                      when you enter into a transaction facilitated by the
                      Zbooni Platform
                    </li>
                    <li>
                      referring websites or third parties, including social
                      media sites
                    </li>
                    <li>
                      when you elect to receive marketing communications from us
                    </li>
                    <li>
                      the maintenance and analysis of our server logs; and
                    </li>
                    <li>
                      “cookies” (small text files placed by us on your
                      computer(s)), single pixel GIF image files, website server
                      log analysis and other similar technological means.
                    </li>
                  </ul>
                </div>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="how-we-use-your-personal-data"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>6.</span>
                  <span>How We Use Your Personal Data</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="how-we-use-your-personal-data"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <div className="pl-6 sm:pl-10">
                  <p className="flex gap-x-5 mb-3">
                    <span>6.1.</span>
                    <span>
                      We will only use your personal data when the law allows us
                      to. Most commonly, we will use your personal data in the
                      following circumstances:
                    </span>
                  </p>
                  <ul className="flex flex-col gap-y-3 list-disc pl-20 sm:pl-24">
                    <li>With your consent.</li>
                    <li>
                      Where we need to perform the contract we are about to
                      enter into or have entered into with you.
                    </li>
                    <li>Where we need to comply with a legal obligation.</li>
                  </ul>
                </div>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>6.2.</span>
                  <span>
                    We have set out below, in a table format, a description of
                    all the ways we plan to use your personal data, and which of
                    the legal bases we rely on to do so.
                  </span>
                </p>
                <div className="pl-6 sm:pl-10">
                  <p className="flex gap-x-5 mb-3">
                    <span>6.3.</span>
                    <span>
                      Note that we may process your personal data for more than
                      one lawful ground depending on the specific purpose for
                      which we are using your data. Please contact us if you
                      need details about the specific legal ground we are
                      relying on to process your personal data where more than
                      one ground has been set out in the table below.
                    </span>
                  </p>
                  <div className="ml-11 overflow-hidden">
                    <div className="text-primary flow-root overflow-x-auto">
                      <div className="border inline-block rounded-md align-middle">
                        <table className="table-fixed divide-y min-w-[700px] w-full max-w-[810px] divide-gray-300">
                          <thead>
                            <tr className="text-sm text-left font-bold divide-x divide-gray-200">
                              <th
                                scope="col"
                                className="whitespace-nowrap rounded-tl-md h-9 px-4 leading-4 bg-secondary-darkest text-white"
                              >
                                Purpose/Activity
                              </th>
                              <th
                                scope="col"
                                className="whitespace-nowrap h-9 px-4 leading-4 bg-secondary-darkest text-white"
                              >
                                Type of Data
                              </th>
                              <th
                                scope="col"
                                className="whitespace-nowrap rounded-tr-md h-9 px-4 leading-4 bg-secondary-darkest text-white"
                              >
                                Lawful basis for processing
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y text-xs font-semibold divide-gray-200 bg-white">
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                To communicate with you (to contact you or
                                provide you with information, alerts and
                                suggestions that are related to the Zbooni
                                Platform; to verify your identity)
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Identity</li>
                                  <li>Contact</li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Consent</li>
                                  <li>Performance of a contract with you</li>
                                </ol>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                To create your account on our Zbooni Platform,
                                to let you log in to your account and use the
                                Zbooni Platform; to manage your account, provide
                                you with support and ensure you are receiving
                                quality service
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Identity</li>
                                  <li>Contact</li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Consent</li>
                                  <li>Performance of a contract with you</li>
                                </ol>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                To facilitate the purchase of products and
                                services offered by Merchants (including but not
                                limited to) through the Zbooni Platform
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Identity</li>
                                  <li>Contact</li>
                                  <li>Financial</li>
                                  <li>Transaction</li>
                                  <li>Marketing and Communications</li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Consent</li>
                                  <li>Performance of a contract with you</li>
                                </ol>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                To manage our relationship with you which will
                                include:
                                <ol className="alphabet">
                                  <li>
                                    Notifying you about changes to our Terms and
                                    Conditions or Data Privacy Policy
                                  </li>
                                  <li>
                                    Asking you to leave a review or take a
                                    survey
                                  </li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Identity</li>
                                  <li>Contact</li>
                                  <li>Profile</li>
                                  <li>Marketing and Communications</li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Consent</li>
                                  <li>Performance of a contract with you</li>
                                  <li>
                                    Necessary to comply with a legal obligation
                                  </li>
                                </ol>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                To review content to detect activity that poses
                                a risk
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Identity</li>
                                  <li>Contact</li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Necessary to protect your interest</li>
                                </ol>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                To deliver relevant content and advertisements
                                to you and measure or understand the
                                effectiveness of the advertising we serve to you
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Identity</li>
                                  <li>Contact</li>
                                  <li>Profile</li>
                                  <li>Usage</li>
                                  <li>Marketing and Communications</li>
                                  <li>Technical</li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Consent</li>
                                  <li>Performance of a contract with you</li>
                                </ol>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                To make suggestions and recommendations to you
                                about services and products that may be of
                                interest to you
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Identity</li>
                                  <li>Contact</li>
                                  <li>Technical</li>
                                  <li>Usage</li>
                                  <li>Profile</li>
                                  <li>Marketing and Communications</li>
                                </ol>
                              </td>
                              <td className="p-4 leading-4">
                                <ol className="alphabet">
                                  <li>Consent</li>
                                  <li>Performance of a contract with you</li>
                                </ol>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>6.4.</span>
                  <span>
                    “<span className="font-bold">Performance of Contract</span>”
                    means processing your personal data where it is necessary
                    for the performance of a contract to which you have
                    consented to be a party or to take steps at your request
                    before entering into such a contract.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="how-we-make-sure-your-personal-data-is-secured"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>7.</span>
                  <span>How We Make Sure Your Personal Data Is Secured</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="how-we-make-sure-your-personal-data-is-secured"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>7.1.</span>
                  <span>
                    To protect your personal data, we take reasonable
                    precautions to try to ensure that it is not inappropriately
                    lost, misused, accessed, disclosed, altered or destroyed.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>7.2.</span>
                  <span>
                    We implement and maintain appropriate technical, security
                    and organisational measures in relation to the processing of
                    your personal data so that the processing will meet the
                    requirements of applicable Data Protection Laws and ensure
                    the protection of your rights.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>7.3.</span>
                  <span>
                    We work with third-party payment processors and payment
                    networks to provide our services. We assume no
                    responsibility or liability for the privacy practices or any
                    other actions of the payment processors or the payment
                    networks or any third-party website, service or service
                    provider and refer to their respective privacy policies.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="retaining-personal-data"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>8.</span>
                  <span>Retaining Personal Data</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel id="retaining-personal-data" className="border-b">
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>8.1.</span>
                  <span>
                    We will only retain your personal data for as long as
                    reasonably necessary to fulfil the purposes we collected it
                    for, including for the purposes of satisfying any legal,
                    regulatory, tax, accounting or reporting requirements. We
                    may retain your personal data for a longer period in the
                    event of a complaint or if we reasonably believe there is a
                    prospect of litigation in respect to our relationship with
                    you.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>8.2.</span>
                  <span>
                    To determine the appropriate retention period for personal
                    data, we consider the amount, nature and sensitivity of the
                    personal data, the potential risk of harm from unauthorised
                    use or disclosure of your personal data, the purposes for
                    which we process your personal data and whether we can
                    achieve those purposes through other means, and the
                    applicable legal, regulatory, tax, accounting or other
                    requirements.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="your-rights"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>9.</span>
                  <span>Your Rights</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel id="your-rights" className="border-b">
              <div className="mb-10 flex flex-col gap-y-7">
                <div className="pl-6 sm:pl-10">
                  <p className="flex gap-x-5 mb-3">
                    <span>9.1.</span>
                    <span>
                      Under certain circumstances, you have rights under
                      applicable Data Protection Laws in relation to your
                      personal data:
                    </span>
                  </p>
                  <ul className="flex flex-col gap-y-3 list-disc pl-14 sm:pl-20">
                    <li>
                      <span className="font-bold">
                        Right to request access to your personal data (commonly
                        known as a &quot;data subject access request&quot;).
                      </span>{" "}
                      This enables you to receive a copy of the personal data we
                      hold about you and to check that we are lawfully
                      processing it.
                    </li>
                    <li>
                      <span className="font-bold">
                        Right to request correction of the personal data that we
                        hold about you.
                      </span>{" "}
                      This enables you to have any incomplete or inaccurate data
                      we hold about you corrected, though we may need to verify
                      the accuracy of the new data you provide to us.
                    </li>
                    <li>
                      <span className="font-bold">
                        Right to request erasure of your personal data.
                      </span>{" "}
                      This enables you to ask us to delete or remove personal
                      data where there is no good reason for us continuing to
                      process it. You also have the right to ask us to delete or
                      remove your personal data where you have successfully
                      exercised your right to object to processing (see below),
                      where we may have processed your information unlawfully or
                      where we are required to erase your personal data to
                      comply with local law. Note, however, that we may not
                      always be able to comply with your request of erasure for
                      specific legal reasons which will be notified to you, if
                      applicable, at the time of your request.
                    </li>
                    <li>
                      <span className="font-bold">
                        Right to object to processing of your personal data.
                      </span>{" "}
                      You have the right to object where we are processing your
                      personal data for example for direct marketing purposes.
                    </li>
                    <li>
                      <span className="font-bold">
                        Right to request restriction of processing of your
                        personal data.
                      </span>
                      This enables you to ask us to suspend the processing of
                      your personal data in the following scenarios:
                      <ul className="dashed pl-6 sm:pl-10">
                        <li>
                          If you want us to establish the data&apos;s accuracy.
                        </li>
                        <li>
                          Where our use of the data is unlawful but you do not
                          want us to erase it.
                        </li>
                        <li>
                          Where you need us to hold the data even if we no
                          longer require it as you need it to establish,
                          exercise or defend legal claims.
                        </li>
                        <li>
                          You have objected to our use of your data but we need
                          to verify whether we have overriding legitimate
                          grounds to use it.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <span className="font-bold">
                        Right to request the transfer of your personal data to
                        you or to a third party.
                      </span>
                      We will provide to you, or a third party you have chosen,
                      your personal data in a structured, commonly used,
                      machine-readable format. Note that this right only applies
                      to automated information which you initially provided
                      consent for us to use or where we used the information to
                      perform a contract with you.
                    </li>
                    <li>
                      <span className="font-bold">
                        Right to withdraw consent at any time where we are
                        relying on consent to process your personal data.
                      </span>{" "}
                      However, this will not affect the lawfulness of any
                      processing carried out before you withdraw your consent.
                      If you withdraw your consent, we may not be able to
                      provide certain services to you. We will advise you if
                      this is the case at the time you withdraw your consent.
                    </li>
                  </ul>
                </div>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>9.2.</span>
                  <span>
                    To make enquiries, exercise any of your rights set out
                    above, or withdraw your consent to the processing of your
                    personal data (where consent is our legal basis for
                    processing your personal data), please contact us via this
                    e-mail address:{" "}
                    <a
                      className="text-blue underline"
                      href="mailto:hello@zbooni.com"
                      lang="en"
                    >
                      hello@zbooni.com
                    </a>
                    .
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>9.3.</span>
                  <span>
                    You will not have to pay a fee to access your personal data
                    (or to exercise any of the other rights). However, we may
                    charge a reasonable fee if your request is clearly
                    unfounded, repetitive or excessive. Alternatively, we may
                    refuse to comply with your request in these circumstances.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>9.4.</span>
                  <span>
                    We may need to request specific information from you to help
                    us confirm your identity and ensure your right to access
                    your personal data (or to exercise any of your other
                    rights). This is a security measure to ensure that personal
                    data is not disclosed to any person who has no right to
                    receive it. We may also contact you to ask you for further
                    information in relation to your request to speed up our
                    response.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>9.5.</span>
                  <span>
                    We try to respond to all legitimate requests within one
                    month. Occasionally it may take us longer than a month if
                    your request is particularly complex or you have made a
                    number of requests. In this case, we will notify you.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>9.6.</span>
                  <span>
                    Whilst we will always work with you to resolve any concerns
                    you have about the use of your personal information, you may
                    have the right to lodge a complaint with the supervisory
                    authority in your country of residence if you have any
                    concerns about our use of your personal information.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>9.7.</span>
                  <span>
                    It is important that the personal data we hold about you is
                    accurate and current. Please keep us informed if your
                    personal data changes during the period for which we hold
                    it.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="protecting-your-fellow-users-privacy-and-intellectual-property-rights"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>10.</span>
                  <span>
                    Protecting your fellow User’s privacy and intellectual
                    property rights
                  </span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="protecting-your-fellow-users-privacy-and-intellectual-property-rights"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>10.1.</span>
                  <span>
                    We respect other people&apos;s rights, and expect you to do
                    the same. You will not post content or take any action on
                    the Zbooni Platform that infringes or violates someone
                    else&apos;s rights, privacy or otherwise violates the law.
                    We can remove any content or information you post on the
                    Zbooni Platform if we believe that it violates this Data
                    Privacy Policy or the Terms and Conditions. You will not use
                    our copyrights or any confusingly similar marks, without our
                    written permission. If you collect information from Users,
                    you will obtain their consent, make it clear you (and not
                    us) are the one collecting their information, and post a
                    privacy policy explaining what information you collect and
                    how you will use it. You will not post anyone&apos;s
                    identification documents or sensitive financial information
                    on the Zbooni Platform. You will not tag fellow Users or
                    send email invitations to non-users without their consent.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="sharing-of-personal-data-with-third-parties"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>11.</span>
                  <span>Sharing of Personal Data With Third Parties</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="sharing-of-personal-data-with-third-parties"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>11.1.</span>
                  <span>
                    We will not sell your personal data to third parties.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>11.2.</span>
                  <span>
                    Your personal data may be disclosed to third parties for one
                    or more of the following purposes:
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>11.3.</span>
                  <span>
                    For business purposes and advertising: We may share a User’s
                    information with a Merchant and a Merchant’s information
                    with a User only as necessary for facilitating your purchase
                    of products and services offered by Merchants. We may also
                    share your information with vendors and service providers,
                    including our data hosting and data storage partners,
                    analytics and advertising providers, and vendors providing
                    technology services and support, payment processing, and
                    data security. We also may share information with
                    professional advisers, such as auditors, law firms, and
                    accounting firms.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>11.4.</span>
                  <span>
                    We may also share data with third parties to whom we may
                    choose to sell, transfer, or merge parts of our business or
                    our assets. Alternatively, we may seek to acquire other
                    businesses or merge with them. If a change happens to our
                    business, then the new owners may use your personal data in
                    the same way as set out in this Data Privacy Policy.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>11.5.</span>
                  <span>
                    We require all third parties to respect the security of
                    personal data and to treat it in accordance with the law. We
                    do not allow our third-party service providers to use your
                    personal data for their own purposes and only permit them to
                    process your personal data for specified purposes and in
                    accordance with our instructions.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>11.6.</span>
                  <span>
                    We will not otherwise share personal information with any
                    third party except where we are permitted to under data
                    protection laws or required to by law.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>11.7.</span>
                  <span>
                    You can opt out of receiving email marketing by
                    unsubscribing using the unsubscribe link provided in all of
                    our marketing email communications.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="international-transfers"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>12.</span>
                  <span>International Transfers</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel id="international-transfers" className="border-b">
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>12.1.</span>
                  <span>
                    The Zbooni Platform is controlled and operated by us from
                    the United Arab Emirates. By accepting the terms of this
                    Privacy Policy you agree that any personal information you
                    provide to us through use of the Zbooni Platform may be
                    stored and processed, transferred between and accessed from
                    the United Arab Emirates and other countries that may not
                    guarantee the same level of protection of personal
                    information as the one in which you reside. However, we will
                    handle your personal information in accordance with this
                    Privacy Policy at all times.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="privacy-disputes"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>13.</span>
                  <span>Privacy Disputes</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel id="privacy-disputes" className="border-b">
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>13.1.</span>
                  <span>
                    You will resolve any claim, cause of action or dispute
                    (claim) you have with us arising out of or relating to this
                    Data Privacy Policy exclusively in the DIFC Courts. The laws
                    of England and Wales will govern this Data Privacy Policy,
                    as well as any claim that might arise between you and us,
                    without regard to conflict of law provisions.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>13.2.</span>
                  <span>
                    If anyone brings a claim against us related to your actions,
                    content or information on our Zbooni Platform, you will
                    indemnify and hold us harmless from and against all damages,
                    losses, and expenses of any kind (including reasonable legal
                    fees and costs) related to such claim.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="cookies-and-other-web-technologies"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>14.</span>
                  <span>Cookies and Other Web Technologies</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel
              id="cookies-and-other-web-technologies"
              className="border-b"
            >
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>14.1.</span>
                  <span>
                    The types of cookies and similar technologies used by us and
                    our partners in connection with the Zbooni Platform can be
                    classified into one of five categories, namely ‘essential
                    website cookies’, ‘functionality cookies’, ‘analytics and
                    performance cookies’, ‘advertising cookies’, and ‘social
                    media cookies’. Cookies do a lot of different tasks to
                    ensure you enjoy your visit to our Zbooni Platform, for
                    example, they are used to remember your preferences on our
                    Zbooni Platform, to help you navigate between pages more
                    efficiently, to enable you to log into your account more
                    easily and to make sure the adverts you see on our Zbooni
                    Platform are relevant to you and your interests. We have set
                    out some further information about each category, and the
                    purposes of the cookies we and third parties set in the
                    following table.
                  </span>
                </p>
                <div className="pl-6 sm:pl-10">
                  <p className="flex gap-x-5 mb-3">
                    <span>14.2.</span>
                    <span>
                      You can block these cookies and similar technologies by
                      changing your browser setting. However, in relation to
                      those that are essential for the operation or improve the
                      functionality of the Zbooni Platform, this may impact your
                      use of the Zbooni Platform.
                    </span>
                  </p>
                  <div className="ml-11 overflow-hidden">
                    <div className="text-primary flow-root overflow-x-auto">
                      <div className="border inline-block rounded-md align-middle">
                        <table className="table-fixed divide-y min-w-[700px] w-full max-w-[810px] divide-gray-300">
                          <thead>
                            <tr className="text-sm text-left font-bold divide-x divide-gray-200">
                              <th
                                scope="col"
                                className="whitespace-nowrap w-1/3 rounded-tl-md h-9 px-4 leading-4 bg-secondary-darkest text-white"
                              >
                                Type of cookie
                              </th>
                              <th
                                scope="col"
                                className="whitespace-nowrap rounded-tr-md h-9 px-4 leading-4 bg-secondary-darkest text-white"
                              >
                                What it does
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y text-xs font-semibold divide-gray-200 bg-white">
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                Cookies necessary for essential website purposes
                              </td>
                              <td className="p-4 leading-4">
                                These cookies are essential to provide you with
                                the Zbooni Platform and any services available
                                through the Zbooni Platform and to use some of
                                its features, such as access to secure areas.
                                Without these cookies, services you have asked
                                for, like transactional pages and secure login
                                accounts, would not be possible.
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                Functionality Cookies
                              </td>
                              <td className="p-4 leading-4">
                                <p className="mb-2">
                                  Functionality cookies record information about
                                  choices you’ve made and allow us to tailor the
                                  Zbooni Platform for you. These cookies mean
                                  that when you continue to use or come back to
                                  the Zbooni Platform, we can provide you with
                                  our services as you have asked for them to be
                                  provided. These cookies allow us to:
                                </p>
                                <ul className="flex flex-col gap-y-2 list-disc pl-6 sm:pl-10">
                                  <li>
                                    Save your location preference if you have
                                    set your location on your homepage, if
                                    applicable, in order to set the time zone
                                  </li>
                                  <li>
                                    Remember settings you have applied, such as
                                    layout, text size, preferences, and colors;
                                    and
                                  </li>
                                  <li>Store accessibility options.</li>
                                </ul>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                Performance Cookies
                              </td>
                              <td className="p-4 leading-4">
                                <p className="flex flex-col gap-y-5">
                                  <span>
                                    We use performance/analytics cookies to
                                    analyze how the Zbooni Platform is accessed,
                                    used, or is performing in order to provide
                                    you with a better user experience and to
                                    maintain, operate and continually improve
                                    the Site.
                                  </span>
                                  <span>
                                    We use Google Analytics, which is a web
                                    analytics tool that helps us understand how
                                    users engage with the Zbooni Platform. Like
                                    many services, Google Analytics uses
                                    first-party cookies to track user
                                    interactions, as in our case, where they are
                                    used to collect information about how users
                                    use our site. This information is used to
                                    compile reports and to help us improve our
                                    Zbooni Platform. The reports disclose Zbooni
                                    Platform trends without identifying
                                    individual visitors. For more information on
                                    Google Analytics, see here. You can opt out
                                    of Google Analytics without affecting how
                                    you visit our site – for more information on
                                    opting out of being tracked by Google
                                    Analytics across all websites you use, visit
                                    this Google page:
                                    https://tools.google.com/dlpage/gaoptout.
                                  </span>
                                  <span>These cookies allow us to:</span>
                                </p>
                                <ul className="flex flex-col gap-y-2 list-disc pl-6 sm:pl-10">
                                  <li>
                                    Better understand our Zbooni Platform
                                    visitors so that we can improve how we
                                    present our content;
                                  </li>
                                  <li>
                                    Test different design ideas for particular
                                    pages, such as our homepage;
                                  </li>
                                  <li>
                                    Collect information about Zbooni Platform
                                    visitors such as where they are located and
                                    what browsers they are using;
                                  </li>
                                  <li>
                                    Determine the number of unique users of the
                                    Zbooni Platform;
                                  </li>
                                  <li>
                                    Improve the Zbooni Platform by measuring any
                                    errors that occur; and
                                  </li>
                                  <li>
                                    Conduct research and diagnostics to improve
                                    service offerings.
                                  </li>
                                </ul>
                              </td>
                            </tr>
                            <tr className="divide-x divide-gray-200 align-top">
                              <td className="p-4 leading-4">
                                Social Media Cookies
                              </td>
                              <td className="p-4 leading-4">
                                On some pages of our Zbooni Platform, third
                                parties that provide applications through our
                                Zbooni Platform will set their own cookies in
                                order to track the success of their applications
                                or customize applications for you. Because of
                                how cookies work, we cannot access these
                                cookies, nor can the third parties access the
                                data in cookies used by us.
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>14.3.</span>
                  <span>
                    You can choose to enable or disable cookies in your internet
                    browser. By default, most internet browsers accept cookies
                    but this can be changed. For further details, please consult
                    the help menu in your internet browser.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>14.4.</span>
                  <span>
                    You can choose to delete cookies at any time; however you
                    may lose any information that enables you to access the
                    Zbooni Platform more quickly and efficiently including, but
                    not limited to, personalisation settings.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>14.5.</span>
                  <span>
                    It is recommended that you ensure that your internet browser
                    is up-to-date and that you consult the help and guidance
                    provided by the developer of your internet browser if you
                    are unsure about adjusting your privacy settings.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>14.6.</span>
                  <span>
                    For more information generally on cookies, including how to
                    disable them, please refer to aboutcookies.org. You will
                    also find details on how to delete cookies from your
                    computer.
                  </span>
                </p>
              </div>
            </AccordionPanel>
            <AccordionItem
              id="final-words-from-zbooni"
              className="flex justify-between h-[124px]"
            >
              <div className="flex items-center">
                <p className="flex gap-x-3 sm:gap-x-6 text-lg font-extrabold sm:text-xl">
                  <span>15.</span>
                  <span>Final Words from Zbooni</span>
                </p>
              </div>
            </AccordionItem>
            <AccordionPanel id="final-words-from-zbooni" className="border-b">
              <div className="mb-10 flex flex-col gap-y-7">
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>15.1.</span>
                  <span>
                    We try to keep Zbooni safe, but you use it at your own risk.
                    We are providing the Zbooni Platform “As Is” without any
                    express or implied warranties. We do not guarantee that
                    Zbooni will be safe or secure. We are not responsible for
                    the actions, content, information, or data of third parties,
                    and you release us, our directors, officers, employees, and
                    agents from any claims and damages, known and unknown,
                    arising out of or in any way connected with any claim you
                    have against any such third parties.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>15.2.</span>
                  <span>
                    We are not responsible for data lost/exposed/used due to the
                    nature of the Internet and digital environments, including
                    illegal actions of hackers and criminals, technical
                    malfunctions of servers and database etc. These are the
                    accepted risks of our Users and users of the Internet in
                    general. You are agreeing to this standard in using our
                    Zbooni Platform.
                  </span>
                </p>
                <p className="flex gap-x-5 pl-6 sm:pl-10">
                  <span>15.3.</span>
                  <span>
                    If you have any questions pertaining to this Data Privacy
                    Policy and its contents, please direct your correspondence
                    to us at{" "}
                    <a
                      className="text-blue underline"
                      href="mailto:hello@zbooni.com"
                      lang="en"
                    >
                      hello@zbooni.com
                    </a>
                    .
                  </span>
                </p>
              </div>
            </AccordionPanel>
          </Accordion>
        </div>
      </div>
    </Layout>
  );
};

export default DataPrivacyPolicy;
